import { productUnits } from '@eneco/enumerations/productUnits';

import { getNettingPeriodYears, NETTING_END_DATE, sumAmounts } from './netting';
import { type NettingDisclosureCopy, nettingDisclosureCopy } from './nettingDisclosure.copy';
import { hasNettingDisclosure, isPricedVolumeVisible, type NettingFigures, type PricedVolume } from './nettingFigures';

export interface NettingDisclosureRow {
  title?: string;
  text?: string;
  value?: string | number;
  highlightText?: boolean;
}

export interface NettingDisclosure {
  summary: { text: string; value: number; popoverContent: string };
  details: NettingDisclosureRow[];
}

export interface NettingDisclosureFormatter {
  euroHighPrecision: (value: number) => string;
  euroFiveDecimals: (value: number) => string;
  electricity: (value: number) => string;
  longDate: (value: string) => string;
}

// Mirrors the consumer layout: a titled row with the volume and the per-kWh rate, followed by the
// `rate × volume =` row carrying the amount. Without a rate there is nothing to multiply, so the amount moves
// onto the titled row instead.
const buildRefundRows = (
  { volume, rate, amount }: PricedVolume,
  year: number,
  copy: NettingDisclosureCopy,
  formatter: NettingDisclosureFormatter,
): NettingDisclosureRow[] => {
  const title = copy.refund(year);
  const volumeText = formatter.electricity(volume);

  if (rate === undefined) {
    return [{ title, text: volumeText, value: amount }];
  }

  return [
    { title, text: volumeText, value: `${formatter.euroHighPrecision(rate)}/${productUnits.electricity}` },
    { text: copy.calculation(formatter.euroFiveDecimals(rate), volumeText), value: amount },
  ];
};

/**
 * Formats netting figures into the B2B redelivery section, laid out like the consumer funnel's but without
 * redelivery costs, which B2B does not have. Returns `undefined` unless {@link hasNettingDisclosure} holds, so
 * the section never renders partially: no period heading without rows beneath it.
 */
export const buildNettingDisclosure = (
  figures: NettingFigures | undefined,
  formatter: NettingDisclosureFormatter,
  copy: NettingDisclosureCopy = nettingDisclosureCopy,
): NettingDisclosure | undefined => {
  if (!hasNettingDisclosure(figures)) {
    return undefined;
  }

  const years = getNettingPeriodYears();
  const { current, future } = figures;
  const isCurrentRefundVisible = isPricedVolumeVisible(current.refund);

  return {
    summary: {
      text: copy.summary,
      value: sumAmounts(isCurrentRefundVisible ? current.refund.amount : undefined, future.refund.amount),
      popoverContent: copy.popover(formatter.longDate(NETTING_END_DATE)),
    },
    details: [
      { text: copy.periodHeading(years.current), highlightText: true },
      ...(current.netted > 0
        ? [{ title: copy.netted(years.current), text: formatter.electricity(current.netted) }]
        : []),
      ...(isCurrentRefundVisible ? buildRefundRows(current.refund, years.current, copy, formatter) : []),

      { text: copy.periodHeading(years.future), highlightText: true },
      ...buildRefundRows(future.refund, years.future, copy, formatter),
    ],
  };
};
